import * as Common from '@piccolohealth/pbs-common';
import { Spin, useRequiredParams } from '@piccolohealth/ui';
import { Settings as DateTimeSettings, P } from '@piccolohealth/util';
import * as Sentry from '@sentry/browser';
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { PiccoloError } from '../components/generic/PiccoloError';
import { useOrganizationMinimalQuery } from '../graphql/hooks/useOrganizationQuery';
import { useAuth } from './AuthContext';
import { useUser } from './UserContext';

export interface OrganizationContext extends Common.Organization {
  organizationMembership: Common.OrganizationMembership;
  hasFeature: (id: Common.FeatureFlag) => boolean;
  isLoading: boolean;
}

export const OrganizationContext = React.createContext<OrganizationContext>(null as any);

export const OrganizationContextProvider = (props: React.PropsWithChildren) => {
  const { children } = props;
  const { organizationId } = useRequiredParams<{ organizationId: string }>();
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const { update: updateIntercom } = useIntercom();
  const user = useUser();

  const organizationMembership = user?.organizationMemberships.find(
    (om) => om.organizationId === organizationId,
  ) as Common.OrganizationMembership;

  const { isLoading, fetchStatus, data, error } = useOrganizationMinimalQuery(
    { organizationId },
    {
      enabled: isAuthenticated && !P.isNil(user) && !P.isNil(organizationMembership),
      retry: false,
    },
  );

  const organization = data?.organization as Common.Organization;

  React.useEffect(() => {
    if (isAuthenticated && !isLoading && user && organization) {
      DateTimeSettings.defaultLocale = organization.locale;
      DateTimeSettings.defaultZone = organization.timezone;

      Sentry.setUser({
        id: user.id,
        username: user.name,
        email: user.email,
        organizationId: organization.id,
      });

      Sentry.setTags({
        username: user.name,
        email: user.email,
        organization: organization.name,
      });

      updateIntercom({
        company: {
          companyId: organization.id,
          name: organization.name,
          customAttributes: {
            roles: organizationMembership?.roles ?? [],
          },
        },
        companies: user.organizationMemberships.map((organizationMembership) => ({
          companyId: organizationMembership.organizationId,
          name: organizationMembership.name,
        })),
      });
      document.title = `Piccolo Baseline - ${organization.name}`;
    }
  }, [organization, user, updateIntercom, isAuthenticated, isLoading, organizationMembership]);

  const hasFeature = (id: Common.FeatureFlag) => {
    return (
      Common.hasFeature(organization?.featureFlags, id) || Common.hasFeature(user?.featureFlags, id)
    );
  };

  const value: OrganizationContext = {
    organizationMembership,
    hasFeature,
    isLoading,
    ...organization,
  };

  const content = P.run(() => {
    if (error) {
      // Handle MFA required error by redirecting to MFA error page
      if (error.type === 'AuthMfaRequiredForOrganization') {
        const returnPath = location.pathname + location.search;
        const organizationName = organizationMembership?.name ?? 'this organization';
        const redirectUrl = `/mfa-organization-error?organizationId=${organizationId}&organizationName=${encodeURIComponent(organizationName)}&return=${encodeURIComponent(returnPath)}`;
        return <Navigate to={redirectUrl} replace />;
      }

      return <PiccoloError error={error} />;
    }

    if (isLoading && fetchStatus !== 'idle') {
      return <Spin />;
    }

    if (!organizationMembership) {
      return (
        <PiccoloError
          error={
            new Common.PiccoloError({
              type: 'Unauthorized',
              message: 'You are not authorized to access this resource',
            })
          }
        />
      );
    }

    if (
      organization?.status === Common.OrganizationStatus.Suspended &&
      !organizationMembership.roles.includes(Common.Role.Support)
    ) {
      return (
        <PiccoloError
          error={
            new Common.PiccoloError({
              type: 'OrganizationSuspended',
              message: 'This organization has been suspended',
            })
          }
        />
      );
    }

    return children;
  });

  return <OrganizationContext.Provider value={value}>{content}</OrganizationContext.Provider>;
};

export const useOrganization: () => OrganizationContext = () => {
  return React.useContext(OrganizationContext);
};
